import React, { createContext, ReactElement, ReactNode, useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import {
  analyzeProductAddToCart,
  analyzeProductAddToWishlist,
  analyzeProductClick,
  analyzeProductsAddToCart,
} from 'b2b-apps/core/analytics/redux';
import { EVENT_SOURCE } from 'b2b-apps/core/analytics/types/types';

interface ProductsAnalyticsContextValue {
  analyzeProductClick: (sku: string, listPosition?: number, additionalId?: string | number) => void;
  analyzeProductAddToCart: (sku: string, listPosition?: number, additionalId?: string | number) => void;
  analyzeProductsAddToCart: (
    products?: { sku: string, listPosition: number}[],
    additionalId?: string | number
  ) => void;
  analyzeProductAddToWishlist: (sku: string) => void;
}

interface ProductsAnalyticsProviderProps {
  children: ReactNode;
  eventSource: EVENT_SOURCE;
}

const ProductsAnalyticsContext = createContext<ProductsAnalyticsContextValue | undefined>(undefined);
export const ProductsAnalyticsProvider = ({ children, eventSource }: ProductsAnalyticsProviderProps): ReactElement => {
  const dispatch = useDispatch();

  return (
    <ProductsAnalyticsContext.Provider value={{
      analyzeProductAddToWishlist: useCallback(
        (sku) => dispatch(analyzeProductAddToWishlist(sku, eventSource)),
        [],
      ),
      analyzeProductClick: useCallback(
        (sku, listPosition, additionalId) => dispatch(analyzeProductClick(
          { sku, additionalId, listPosition, eventSource },
        )),
        [],
      ),
      analyzeProductAddToCart: useCallback(
        (sku, listPosition, additionalId) => dispatch(analyzeProductAddToCart(
          { sku, listPosition, additionalId, eventSource },
        )),
        [],
      ),
      analyzeProductsAddToCart: useCallback(
        (products, additionalId) => dispatch(analyzeProductsAddToCart(
          { products, additionalId, eventSource },
        )),
        [],
      ),
    }}>
      {children}
    </ProductsAnalyticsContext.Provider>
  );
};

export const useProductsAnalytics = (): ProductsAnalyticsContextValue => {
  const context = useContext(ProductsAnalyticsContext);

  if (context === undefined) {
    throw new Error('useProductsAnalytics must be used within a ProductsAnalyticsProvider');
  }

  return context;
};
